import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Lock,
  Moon,
  Sun,
  Monitor,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { useTheme } from "../../app/ThemeContext";
import { deleteMe } from "../../lib/api";

interface SettingsPageProps {
  onBackClick?: () => void;
}

export default function SettingsPage({ onBackClick }: SettingsPageProps) {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();

  const { theme, setTheme } = useTheme();
  const [language, setLanguage] = useState("pt-BR");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleChangePassword = async () => {
    if (!user?.email) return;
    
    try {
      setIsSendingEmail(true);
      const { recoverPassword } = await import("../../lib/api/auth");
      await recoverPassword(user.email);
      alert("Um link de redefinição de senha foi enviado para o seu email.");
    } catch (error) {
      console.error("Erro ao enviar email de recuperação:", error);
      alert("Erro ao enviar email de recuperação. Tente novamente.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!token) {
      alert("Você precisa estar logado para deletar a conta.");
      return;
    }
    try {
      setDeleteLoading(true);
      await deleteMe(token);
      logout();
      setShowDeleteModal(false);
      if (onBackClick) onBackClick();
    } catch (e) {
      console.error(e);
      alert("Não foi possível deletar a conta. Tente novamente.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-(--bg-secondary)">
      <header className="bg-(--header-bg) shadow-sm border-b border-(--border-color) sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            {onBackClick && (
              <button
                onClick={onBackClick}
                className="flex items-center gap-2 px-3 py-2 hover:bg-(--bg-accent) rounded-lg transition font-medium text-(--text-tertiary)"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Voltar</span>
              </button>
            )}
            <h1 className="text-xl font-semibold text-(--text-primary)">
              Configurações
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color) p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-(--bg-accent)">
                <Monitor className="w-5 h-5 text-(--purple-color)" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-(--text-primary)">
                  Aparência
                </h3>
                <p className="text-sm text-(--text-tertiary)">
                  Personalize a aparência da plataforma
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-3 text-(--text-primary)">
                  Tema
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => setTheme("light")}
                    className={`p-4 border-2 rounded-lg transition ${
                      theme === "light"
                        ? "border-(--primary-color) bg-(--bg-accent)"
                        : "border-(--border-color) hover:border-(--text-tertiary)"
                    }`}
                  >
                    <Sun
                      className={`w-6 h-6 mx-auto mb-2 ${theme === "light" ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                    />
                    <p
                      className={`text-sm font-medium ${theme === "light" ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                    >
                      Claro
                    </p>
                  </button>

                  <button
                    onClick={() => setTheme("dark")}
                    className={`p-4 border-2 rounded-lg transition ${
                      theme === "dark"
                        ? "border-(--primary-color) bg-(--bg-accent)"
                        : "border-(--border-color) hover:border-(--text-tertiary)"
                    }`}
                  >
                    <Moon
                      className={`w-6 h-6 mx-auto mb-2 ${theme === "dark" ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                    />
                    <p
                      className={`text-sm font-medium ${theme === "dark" ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                    >
                      Escuro
                    </p>
                  </button>

                  <button
                    onClick={() => setTheme("system")}
                    className={`p-4 border-2 rounded-lg transition ${
                      theme === "system"
                        ? "border-(--primary-color) bg-(--bg-accent)"
                        : "border-(--border-color) hover:border-(--text-tertiary)"
                    }`}
                  >
                    <Monitor
                      className={`w-6 h-6 mx-auto mb-2 ${theme === "system" ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                    />
                    <p
                      className={`text-sm font-medium ${theme === "system" ? "text-(--primary-color)" : "text-(--text-tertiary)"}`}
                    >
                      Sistema
                    </p>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3 text-(--text-primary)">
                  Idioma
                </label>
                <select
                  value={language}
                  onChange={(e) => {
                    if (e.target.value === "pt-BR") setLanguage(e.target.value);
                  }}
                  className="w-full px-4 py-3 border border-(--border-color) rounded-lg focus:outline-none focus:ring-2 bg-(--input-bg) text-(--text-primary) transition"
                >
                  <option value="pt-BR">Português (Brasil)</option>
                  <option value="en-US" disabled>
                    English (US) - em breve
                  </option>
                  <option value="es-ES" disabled>
                    Español - em breve
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--border-color) p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-(--bg-accent)">
                <Lock className="w-5 h-5 text-(--warning-color)" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-(--text-primary)">
                  Segurança
                </h3>
                <p className="text-sm text-(--text-tertiary)">
                  Gerencie a segurança da sua conta
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleChangePassword}
                disabled={isSendingEmail}
                className="w-full flex items-center justify-between p-4 border border-(--border-color) rounded-lg hover:bg-(--bg-accent) transition text-left disabled:opacity-60"
              >
                <div>
                  <p className="font-medium text-(--text-primary)">
                    {isSendingEmail ? "Enviando email..." : "Alterar Senha"}
                  </p>
                  <p className="text-sm text-(--text-tertiary)">
                    {isSendingEmail 
                      ? "Verifique sua caixa de entrada em instantes" 
                      : user?.updatedAt   
                        ? `Última alteração em ${new Date(user.updatedAt).toLocaleDateString('pt-BR')}`
                        : "Última alteração não disponível"}
                  </p>
                </div>
                <ArrowLeft className="w-5 h-5 rotate-180 text-(--text-tertiary)" />
              </button>

              <div className="p-4 border border-(--border-color) rounded-lg bg-(--bg-secondary)">
                <p className="text-sm mb-2 text-(--text-primary)">
                  <strong>Email de recuperação:</strong> {user?.email}
                </p>
                <p className="text-sm text-(--text-tertiary)">
                  Use este email para recuperar sua senha
                </p>
              </div>
            </div>
          </div>

          <div className="bg-(--card-bg) rounded-xl shadow-sm border border-(--danger-color) p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-(--bg-accent)">
                <AlertTriangle className="w-5 h-5 text-(--danger-color)" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-(--danger-color)">
                  Zona de Perigo
                </h3>
                <p className="text-sm text-(--text-tertiary)">
                  Ações irreversíveis (recuperação disponível por 30 dias)
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-3 px-6 py-3 border-2 border-(--danger-color) text-(--danger-color) rounded-lg font-medium transition hover:bg-(--bg-accent)"
            >
              <Trash2 className="w-5 h-5" />
              Deletar Conta
            </button>
          </div>
        </div>
      </main>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-(--card-bg) border border-(--border-color) rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-(--bg-accent)">
                <AlertTriangle className="w-6 h-6 text-(--danger-color)" />
              </div>
              <h3 className="text-xl font-bold text-(--text-primary)">
                Deletar Conta
              </h3>
            </div>

            <p className="mb-6 text-(--text-tertiary)">
              Tem certeza que deseja deletar sua conta? Sua conta pode ser{" "}
              <strong>recuperada em até 30 dias</strong>. Após este período,
              esta ação tornas-se irreversível e todos os seus dados serão
              permanentemente apagados.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2.5 border border-(--border-color) text-(--text-tertiary) rounded-lg font-medium hover:bg-(--bg-accent) transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 bg-(--danger-color) text-white rounded-lg font-medium transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleteLoading ? "Deletando..." : "Sim, Deletar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
